import { TOUR_STEPS } from './tourSteps';
import { Role, userHasRole } from './roles';

// A member with several roles gets the tour of the surface they land on first.
const TOUR_ROLES = [Role.ADMIN, Role.SUPPORT_AGENT, Role.SLEEP_COACH];

export const tourRoleFor = (user) => TOUR_ROLES.find((role) => userHasRole(user, role)) || 'staff';

export const stepsFor = (user) => TOUR_STEPS[tourRoleFor(user)];

export const onTourScreen = (step, pathname) =>
  pathname === step.path || pathname.startsWith(`${step.path}/`);

// The consoles and Insights use the DUSQ kit; the dashboard shell keeps theme.css.
export const tourSurface = (pathname) =>
  /^\/dashboard\/(insights|coach\/|ops\/|support\/console|profile)/.test(pathname)
    ? 'console'
    : 'admin';

export const isTourTargetShown = (element) => {
  const box = element.getBoundingClientRect();
  if (!box.width || !box.height) return false;
  if (getComputedStyle(element).visibility === 'hidden') return false;
  // Off-canvas drawers keep their size but sit outside the viewport.
  return (
    box.right > 0 && box.bottom > 0 && box.left < window.innerWidth && box.top < window.innerHeight
  );
};

// Alternates share an id (a drawer and the button that opens it); the one on screen wins.
export const findTourTarget = (id) =>
  [...document.querySelectorAll(`[data-tour="${id}"]`)].find(isTourTargetShown) || null;

// Resolves with the element once it shows up, or null when `timeoutMs` passes first.
export const waitForTourTarget = (id, timeoutMs) => {
  let settle;
  const promise = new Promise((resolve) => {
    settle = resolve;
  });
  let timer;
  const observer = new MutationObserver(() => {
    const element = findTourTarget(id);
    if (element) finish(element);
  });
  function finish(element) {
    observer.disconnect();
    clearTimeout(timer);
    settle(element);
  }
  timer = setTimeout(() => finish(null), timeoutMs);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
  });
  return { promise, cancel: () => finish(null) };
};

const GAP = 12;
const GUTTER = 16;
const ARROW_INSET = 20;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

// Fixed coordinates for the card next to `spot`, trying below, above, right, then left;
// with no room on any side (a full-screen target at phone width) it centres instead.
export const placeCard = (spot, size, viewport) => {
  const maxLeft = viewport.width - size.width - GUTTER;
  const maxTop = viewport.height - size.height - GUTTER;
  const centred = {
    side: 'center',
    top: clamp((viewport.height - size.height) / 2, GUTTER, maxTop),
    left: clamp((viewport.width - size.width) / 2, GUTTER, maxLeft),
  };
  if (!spot) return centred;
  const fits = {
    bottom: spot.top + spot.height + GAP + size.height <= viewport.height - GUTTER,
    top: spot.top - GAP - size.height >= GUTTER,
    right: spot.left + spot.width + GAP + size.width <= viewport.width - GUTTER,
    left: spot.left - GAP - size.width >= GUTTER,
  };
  const side = ['bottom', 'top', 'right', 'left'].find((candidate) => fits[candidate]);
  if (!side) return centred;
  const vertical = side === 'bottom' || side === 'top';
  const top = vertical
    ? side === 'bottom'
      ? spot.top + spot.height + GAP
      : spot.top - GAP - size.height
    : clamp(spot.top, GUTTER, maxTop);
  const left = vertical
    ? clamp(spot.left, GUTTER, maxLeft)
    : side === 'right'
      ? spot.left + spot.width + GAP
      : spot.left - GAP - size.width;
  // The pointer sits on the edge facing the target, at the target's centre line.
  const arrow = vertical
    ? clamp(spot.left + spot.width / 2 - left, ARROW_INSET, size.width - ARROW_INSET)
    : clamp(spot.top + spot.height / 2 - top, ARROW_INSET, size.height - ARROW_INSET);
  return { side, top, left, arrow };
};
